import {useState, useCallback} from 'react';
import {CApi as adminGwApi} from '@services/api';
import {AdminClient} from '@services/admin-gw';

function useClientOptions(userLogin?: string) {
    const [items, setItems] = useState<AdminClient[]>([]);
    const [total, setTotal] = useState(0);
    const [offset, setOffset] = useState(0);
    const [limit] = useState(20);

    const [searchText, setSearchText] = useState('');

    const [isLoading, setIsLoading] = useState(false);
    const [isDataLoaded, setIsDataLoaded] = useState(false);

    const hasMore = items.length < total;

    const loadData = useCallback(
        async (append: boolean = false, q: string = '') => {
            if (!userLogin) return;
            try {
                setIsLoading(true);

                const query: {
                    q?: string;
                    role?: 'impersonate' | 'suggest' | 'view';
                    limit: number;
                    offset: number;
                } = {
                    limit,
                    offset: append ? offset : 0
                };

                const trimmedQ = q.trim();
                if (trimmedQ && trimmedQ.length >= 1) {
                    query.q = trimmedQ;
                }

                const response = await adminGwApi().admin.clientList(userLogin, query);

                const newItems = Array.isArray(response.data.items)
                    ? response.data.items
                    : Object.values(response.data.items);

                const newTotal = response.data.total ?? 0;

                setItems((prev) => (append ? [...prev, ...newItems] : newItems));
                setTotal(newTotal);

                if (append) {
                    setOffset((prevOffset) => prevOffset + newItems.length);
                } else {
                    setOffset(newItems.length);
                }

                setIsDataLoaded(true);
            } catch (e) {
                console.error('Ошибка при загрузке данных:', e);
            } finally {
                setIsLoading(false);
            }
        },
        [userLogin, limit, offset]
    );

    const loadInitialData = useCallback(async () => {
        setOffset(0);
        await loadData(false, searchText);
    }, [loadData, searchText]);

    const loadMoreData = useCallback(async () => {
        if (hasMore && !isLoading) {
            await loadData(true, searchText);
        }
    }, [hasMore, isLoading, loadData, searchText]);

    const handleSearchChange = useCallback(
        async (text: string) => {
            setSearchText(text);
            setOffset(0);
            await loadData(false, text);
        },
        [loadData]
    );

    return {
        items,
        total,
        isLoading,
        isDataLoaded,
        hasMore,
        loadInitialData,
        loadMoreData,
        handleSearchChange,
        searchText
    };
}

export default useClientOptions;
